import Guard from 'arg-guard'

import MathTools from '@/helpers/math-tools'

import METRIC_KEYS from './METRIC_KEYS'

const getSavingsValue = function(current, projected, cfg) {
    return cfg.invertSavingsCalculation
        ? projected - current
        : current - projected
}

/**
 * Defined structure of a metric value.
 * @export MetricValue
 * @class MetricValue
 */
const Value = class MetricValue {

    /**
     * Creates an instance of MetricValue.
     * @param {string} measure
     * @param {number} value
     * @param {boolean} [measureAsPrefix=false]
     * @param {boolean} [spaceAroundMeasure=false]
     * @param {number} [fixDecimalPlaces=0]
     * @param {?number} [displayMultiplier=null]
     * @param {?string} [displayForZero=null]
     */
    constructor(measure = '', value = 0, { measureAsPrefix = false, spaceAroundMeasure = false, fixDecimalPlaces = 0, displayMultiplier = null, displayForZero = null } = {
        measureAsPrefix: false, spaceAroundMeasure: false, fixDecimalPlaces: 0, displayMultiplier: null, displayForZero: null }) {
        Guard.throwIf(measure, 'measure')

        this.value = value
        this._config = { measure, measureAsPrefix, spaceAroundMeasure, fixDecimalPlaces, displayMultiplier, displayForZero }
    }

    /**
     * Gets the display text associated with the provided value.
     * @type {string}
     * @readonly
     */
    get display() {
        const cfg = this._config
        const space = cfg.spaceAroundMeasure ? ' ' : ''
        const value = cfg.displayMultiplier ? this.value * cfg.displayMultiplier : this.value
        const stringValue = value < 0
            ? value.toFixed(cfg.fixDecimalPlaces).replace('-', '')
            : value.toFixed(cfg.fixDecimalPlaces)
        const displayValueWithoutNegativity = cfg.measureAsPrefix
            ? `${ cfg.measure }${ space }${ stringValue }`
            : `${ stringValue }${ space }${ cfg.measure }`

        return cfg.displayForZero !== null && value === 0
            ? cfg.displayForZero
            : value < 0
                ? `-${ displayValueWithoutNegativity }`
                : displayValueWithoutNegativity
    }
}

/**
 * Defined structure of a metric.
 * @export Metric
 * @class Metric
 */
export default class Metric {

    /**
     * Creates an instance of Metric.
     * @param {string} key
     * @param {string} name
     * @param {string} measure
     * @param {string} icon
     * @param {number} initialValue
     * @param {?number} projectedValue
     * @param {Boolean} measureAsPrefix
     * @param {Boolean} spaceAroundMeasure
     * @param {number} fixDecimalPlaces
     * @param {Boolean} invertSavingsCalculation
     */
    constructor(key, name, measure, icon,
        { initialValue = 0, projectedValue = null,
            measureAsPrefix = false, spaceAroundMeasure = false, fixDecimalPlaces = 0, invertSavingsCalculation = false } = {
            initialValue: 0, projectedValue: null,
            measureAsPrefix: false, spaceAroundMeasure: false, fixDecimalPlaces: 0, invertSavingsCalculation: false }) {
        Guard.throwIf(key, 'key')
        Guard.throwIf(name, 'name')
        Guard.throwIf(measure, 'measure')

        if (projectedValue == null) projectedValue = initialValue

        this.key = key
        this.name = name
        this.measure = measure
        this.icon = icon

        this._config = { measureAsPrefix, spaceAroundMeasure, fixDecimalPlaces, invertSavingsCalculation, displayForZero: 'none' }

        this.current = new Value(measure, initialValue, this._config)
        this.projected = new Value(measure, projectedValue, this._config)
    }

    /**
     * Determines if the application of measures has made the value of the metric worse.
     * @readonly
     * @returns {Boolean}
     */
    get hasWorsened() {
        return this._config.invertSavingsCalculation
            ? this.projected.value < this.current.value
            : this.projected.value > this.current.value
    }

    /**
     * The percentage difference between projections and the current value of the metric.
     * @readonly
     * @returns {MetricValue}
     */
    get percentageSaving() {
        const value = MathTools.round(getSavingsValue(this.current.value, this.projected.value, this._config) / this.current.value, 2)
        const percentageConfig = {
            displayMultiplier: 100,
            displayForZero: 'none',
        }

        return new Value('%', value, percentageConfig)
    }

    /**
     * The difference between projections and the current value of the metric.
     * @readonly
     * @returns {MetricValue}
     */
    get saving() {
        const value = MathTools.round(getSavingsValue(this.current.value, this.projected.value, this._config), this._config.fixDecimalPlaces)
        return new Value(this.measure, value, this._config)
    }

    /**
     * Returns the object in a form suitable to store.
     * @returns {MetricAsJson}
     */
    toStoreJson() {
        return {
            key: this.key,
            current: this.current ? this.current.value : null,
            projected: this.projected ? this.projected.value : null,
        }
    }

    /**
     * Creates a Carbon Dioxide metric.
     * @static
     * @param {?number} current Optional base value for the metric.
     * @param {?number} projected Optional projected value for the metric. If missing, the value of current is used
     * @returns {Metric} A metric configured for storing CO₂ data.
     */
    static carbonDioxide(current = 0, projected = null) {
        return new Metric(METRIC_KEYS.carbonSaving, 'Carbon Dioxide', 'tCO₂', 'leaf', {
            spaceAroundMeasure: true,
            fixDecimalPlaces: 2,
            initialValue: current,
            projectedValue: projected,
        })
    }

    /**
     * Creates a Fuel Bill metric.
     * @static
     * @param {?number} current Optional base value for the metric.
     * @param {?number} projected Optional projected value for the metric. If missing, the value of current is used
     * @returns {Metric} A metric configured for storing Fuel Bill data.
     */
    static fuelBill(current = 0, projected = null) {
        return new Metric(METRIC_KEYS.fuelBill, 'Fuel Bill', '£', 'pound-sign', {
            measureAsPrefix: true,
            initialValue: current,
            projectedValue: projected,
        })
    }

    /**
     * Creates a SAP Score metric.
     * @static
     * @param {?number} current Optional base value for the metric.
     * @param {?number} projected Optional projected value for the metric. If missing, the value of current is used
     * @returns {Metric} A metric configured for storing SAP Score data.
     */
    static sapScore(current = 0, projected = null) {
        return new Metric(METRIC_KEYS.sapScore, 'EPC Score', '', 'star', {
            fixDecimalPlaces: 2,
            initialValue: current,
            projectedValue: projected,
            invertSavingsCalculation: true,
        })
    }

    /**
     * Builds a metric from a key.
     * @param {string} key The metric key.
     * @param {?number} current Optional base value for the metric.
     * @param {?number} projected Optional projected value for the metric. If missing, the value of current is used
     * @returns {Metric} A metric configuration to match the provided key.
     */
    static fromKey(key, current = 0, projected = null) {
        Guard.throwIf(key, 'key')
        if (projected == null) projected = current

        if (key === METRIC_KEYS.carbonSaving)
            return Metric.carbonDioxide(current, projected)
        else if (key === METRIC_KEYS.fuelBill)
            return Metric.fuelBill(current, projected)
        else if (key === METRIC_KEYS.sapScore)
            return Metric.sapScore(current, projected)

        throw new TypeError(`Invalid Metric Key '${ key }' ${ current }`)
    }

    /**
     * Builds a metric from data.
     * @param {MetricAsJson} data The metric data.
     * @returns {Metric} A metric configuration to match the provided data.
     */
    static fromData(data) {
        Guard.throwIf(data, 'data')
        if (!data.key) throw new TypeError('The data parameter must contain a key')
        return Metric.fromKey(data.key, data.current, data.projected)
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @param {?MetricAsJson} data The stored data.
     * @returns {Metric}
     */
    static fromStoreJson(data = null) {
        return data ? Metric.fromData(data) : null
    }
}

export {
    Value
}
